import { ChangeDetectionStrategy, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { User, userToken } from 'go-modules/models/user/user.factory';
import { SsoProviderButtonModule } from '@go-modules/components/sso-provider-button/sso-provider-button.module';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { A11yModule } from '@angular/cdk/a11y';
import { PasswordVisibilityToggleModule } from '../../../directives/password-visibility-toggle/password-visibility-toggle.module';
import { UserCredentials } from '@admin-tools/app/common/interfaces/user/credentials';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FocusOnFirstInvalidFieldModule } from '@go-modules/directives/focus-on-first-invalid-field';
import { AccountLockoutDialogComponent } from '../account-lockout-dialog/account-lockout-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SessionManagerService, sessionManagerToken } from 'go-modules/session-manager/session-manager.service';

@Component({
	selector: 'session-timeout-dialog',
	templateUrl: './session-timeout.component.html',
	styleUrls: ['./session-timeout.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		A11yModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		ReactiveFormsModule,
		TranslateModule,
		SsoProviderButtonModule,
		PasswordVisibilityToggleModule,
		FocusOnFirstInvalidFieldModule
	]
})
export class SessionTimeoutDialogComponent implements OnInit, OnDestroy {
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public formError$: BehaviorSubject<string> = new BehaviorSubject(null);
	public form: FormGroup;
	public readonly email = new FormControl('', [Validators.required, Validators.email, Validators.maxLength(255)]);
	public readonly password = new FormControl('', Validators.required);
	private readonly destroy$ = new Subject();

	constructor (
		public dialogRef: GoDialogRef,
		private fb: FormBuilder,
		private ngxAuthService: NgxAuthService,
		private translateService: TranslateService,
		private dialog: MatDialog,
		@Inject(GO_MODAL_DATA) public readonly data: {
			user: UserCredentials;
		},
		@Inject('Window') private readonly window: Window,
		@Inject(sessionManagerToken) private sessionManager: SessionManagerService,
		@Inject(userToken) private readonly userModel: ReturnType<typeof User>
	) {}

	public ngOnInit (): void {
		this.initializeForm();
	}

	public ngOnDestroy (): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	private initializeForm (): void {
		this.form = this.fb.group({
			email: this.email,
			password: this.password
		});

		if (this.data.user?.username) {
			this.email.setValue(this.data.user.username);
		}
	}

	public login (): void {
		if (!this.form.valid) {
			this.form.markAllAsTouched();
			return;
		}

		this.loading$.next(true);
		this.ngxAuthService
			.login(this.email.value, this.password.value)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (userInfo) => {
					this.dialogRef.close(this.userModel.model(userInfo.user));
					this.loading$.next(false);
				},
				error: (error) => {
					switch (error.status) {
						case 423:
							const accountLockTime = error.error.remainingAccountLockSeconds;
							const remainingAccountLockTimeMinutes =
								this.sessionManager.convertToMinutes(accountLockTime);
							this.showAccountLockedModal(remainingAccountLockTimeMinutes);
							this.formError$.next('common-validation_user-password-incorrect');
							break;
						case 410:
							this.formError$.next('common-validation_sso-exists-on-login');
							break;
						case 400:
							this.formError$.next('common-validation_user-password-incorrect');
							break;
						default:
							this.formError$.next('unknown-error-description');
					}

					this.formError$.next(this.translateService.instant(this.formError$.value));
					this.loading$.next(false);
				}
			});
	}

	public loginWithSso (provider: string): void {
		this.ngxAuthService
			.getAuthUrl(provider, { redirect: this.window.location.href })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ url }: { url: string }) => {
					this.window.location.href = url;
				},
				error: () => {
					this.formError$.next(
						this.translateService.instant('common_an-unexpected-error-occurred')
					);
				}
			});
	}

	private showAccountLockedModal (remainingTime): void {
		const modalData = {
			message: this.translateService.instant('modal-account-locked-message', {
				number: remainingTime
			}),
			forgotPasswordUrl: '/dashboard/auth/forgot'
		};
		this.dialog.open(AccountLockoutDialogComponent, { data: modalData });
	}
}
